import { useState, useCallback, useRef, ReactElement } from "react";
import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
  GridCellProps,
  GridRowProps,
} from "@progress/kendo-react-grid";
import type {
  ILitigatorCase,
  ILitigatorCaseDetails,
} from "src/interfaces/ComplianceGuide/ILitigatorSearch";
import CaseDetails from "./CaseDetails";
import styles from "./LitigatorSearch.module.css";

// Number of visible GridColumns — keep in sync with the columns below
const COLUMN_COUNT = 7;

interface ResultsTableProps {
  cases: ILitigatorCase[];
  onDownloadPdf: (caseId: string) => void;
  downloadingIds: Set<string>;
  /**
   * Requests the full details for a case, including its summary. Called when a row opens.
   * Idempotent, so this table does not track which cases have already been fetched.
   */
  onRequestCaseDetails: (caseId: string) => void;
  /** Details loaded so far, keyed by encrypted caseId; absent until a row has opened. */
  caseDetails: Record<string, ILitigatorCaseDetails>;
  loadingDetailIds: Set<string>;
  skip: number;
  take: number;
  total: number;
  onPageChange: (skip: number) => void;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  cases,
  onDownloadPdf,
  downloadingIds,
  onRequestCaseDetails,
  caseDetails,
  loadingDetailIds,
  skip,
  take,
  total,
  onPageChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // The toggle logic reads this ref rather than the state above, and every mutation goes
  // through toggleExpand, so the two never diverge. Reading the state directly would put it
  // in toggleExpand's dependencies, changing that callback's identity on every expand — and
  // with it CaseTitleCell/ExpandCell, which Kendo takes as component types, so the cells
  // would remount and drop keyboard focus mid-navigation.
  const expandedIdsRef = useRef(expandedIds);

  const toggleExpand = useCallback(
    (caseId: string) => {
      const isOpening = !expandedIdsRef.current.has(caseId);

      const next = new Set(expandedIdsRef.current);
      if (isOpening) {
        next.add(caseId);
      } else {
        next.delete(caseId);
      }
      expandedIdsRef.current = next;
      setExpandedIds(next);

      // Only on open, and outside any state updater (React may invoke those twice in
      // development). Requesting on close too would defeat the container's retry-on-failure
      // guard: a row whose fetch failed would re-request as the user closed it, and a late
      // success would log a ViewDetailsPage event for a panel that is no longer open.
      if (isOpening) onRequestCaseDetails(caseId);
    },
    [onRequestCaseDetails],
  );

  // Add expanded flag to each row for the caret icon direction
  const dataWithExpanded = cases.map((c) => ({
    ...c,
    expanded: expandedIds.has(c.caseId),
  }));

  // Case Title cell — clickable/keyboard-accessible to toggle detail row
  const CaseTitleCell = useCallback(
    (props: GridCellProps) => (
      <td
        className={styles["case-title-link"]}
        role="button"
        tabIndex={0}
        aria-label={`${props.dataItem.caseTitle}, ${props.dataItem.expanded ? "collapse" : "expand"} details`}
        aria-expanded={props.dataItem.expanded}
        onClick={() => toggleExpand(props.dataItem.caseId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpand(props.dataItem.caseId);
          }
        }}
        style={{ cursor: "pointer" }}
      >
        {props.dataItem.caseTitle}
      </td>
    ),
    [toggleExpand],
  );

  // Custom expand/collapse caret column (right side, matching MVC style)
  const ExpandCell = useCallback(
    (props: GridCellProps) => (
      <td
        role="button"
        tabIndex={0}
        aria-label={props.dataItem.expanded ? "Collapse row" : "Expand row"}
        aria-expanded={props.dataItem.expanded}
        onClick={() => toggleExpand(props.dataItem.caseId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpand(props.dataItem.caseId);
          }
        }}
        style={{ cursor: "pointer", textAlign: "center" }}
      >
        <i
          className={`bi ${props.dataItem.expanded ? "bi-caret-up-fill" : "bi-caret-down-fill"}`}
          aria-hidden="true"
        />
      </td>
    ),
    [toggleExpand],
  );

  const handlePageChange = useCallback(
    (event: GridPageChangeEvent) => {
      onPageChange(event.page.skip);
    },
    [onPageChange],
  );

  // Render detail row below expanded rows — avoids Kendo's built-in
  // hierarchy column so there is no phantom left-side gap.
  const rowRender = useCallback(
    (
      trElement: ReactElement<HTMLTableRowElement>,
      rowProps: GridRowProps,
    ) => {
      const dataItem = rowProps.dataItem as ILitigatorCase & {
        expanded: boolean;
      };
      if (!dataItem.expanded) return trElement;

      return (
        <>
          {trElement}
          <tr key={`${dataItem.caseId}-detail`} className={styles["detail-row"]}>
            <td colSpan={COLUMN_COUNT} style={{ padding: 0 }}>
              <CaseDetails
                caseData={dataItem}
                details={caseDetails[dataItem.caseId] ?? null}
                isLoadingDetails={loadingDetailIds.has(dataItem.caseId)}
                onDownloadPdf={onDownloadPdf}
                isDownloading={downloadingIds.has(dataItem.caseId)}
              />
            </td>
          </tr>
        </>
      );
    },
    [onDownloadPdf, downloadingIds, caseDetails, loadingDetailIds],
  );

  if (cases.length === 0) return null;

  return (
    <div className={styles["results-grid"]}>
      <Grid
        data={dataWithExpanded}
        pageable
        scrollable="none"
        skip={skip}
        take={take}
        total={total}
        onPageChange={handlePageChange}
        rowRender={rowRender}
      >
        <GridColumn field="caseTitle" title="Case Title" cell={CaseTitleCell} />
        <GridColumn field="caseNumber" title="Case Number" />
        <GridColumn field="plaintiffs" title="Plaintiff" />
        <GridColumn field="defendants" title="Defendant" />
        <GridColumn field="filingDate" title="Filing Date" />
        <GridColumn field="cause" title="Cause" />
        <GridColumn cell={ExpandCell} width="40px" title=" " />
      </Grid>
    </div>
  );
};

export default ResultsTable;
